(() => {
  'use strict';
  if (window.toscaniMotion) return;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const fine = matchMedia('(hover: hover) and (pointer: fine)');
  const ease = 'cubic-bezier(.16,1,.3,1)';
  const primary = '.ts-claims-button,.tm-primary,.tc-button,.button:not(.button-secondary):not(.button-unstyled)';
  const secondary = '.tb-card-link,.ts-categories a,.tm-secondary,.ts-welcome-secondary,.ts-footer-catalog,.button-secondary,.header__icon,slideshow-controls button,quantity-selector button';
  const controls = `${primary},${secondary}`;
  const cards = '.product-card,.ts-collection-tile,.tc-steps > li';
  const entrances = '.ts-collection-tile,.ts-catalog-heading,.ts-categories,.product-details,.product-card,.ts-welcome-art,.ts-welcome .ts-eyebrow,.ts-welcome-description,.ts-welcome .ts-claims-button,.ts-welcome-secondary,.ts-orbit-actions,.tm-hero-actions,.ts-my-claims > .ts-claims-button';
  const running = new Set();
  const surfaces = new Map();
  const hovering = new Map();
  const seen = new WeakSet();
  const observed = new Set();
  let disabled = new URLSearchParams(location.search).get('toscani_motion') === 'off';
  let tilt = null;
  let tiltFrame = 0;
  const enabled = () => !disabled && !reduced.matches && !document.hidden;
  const usable = el => el instanceof HTMLElement && !el.matches(':disabled,[aria-disabled="true"]') && !el.closest('[inert]');
  const closest = (event, selector) => event.target instanceof Element ? event.target.closest(selector) : null;

  // All animation effects are removed on completion; original CSS wins again.
  function play(el, frames, options = {}, hold = false) {
    if (!enabled() || !el?.animate || !el.isConnected) return null;
    let animation;
    try { animation = el.animate(frames, { duration: 440, easing: ease, ...options }); }
    catch { return null; }
    running.add(animation);
    animation.oncancel = () => running.delete(animation);
    animation.finished.then(() => {
      if (!hold) { running.delete(animation); animation.cancel(); }
    }, () => running.delete(animation));
    return animation;
  }
  function move(el, frames, options = {}, hold = false) {
    return play(el, frames.map(transform => ({ transform })), { composite: 'add', ...options }, hold);
  }

  // Temporary, aria-hidden light layers never receive a click or alter spacing.
  function light(el, type, event, subtle = false) {
    if (!enabled() || !usable(el)) return;
    let host = surfaces.get(el);
    if (!host) {
      const hadStyle = el.hasAttribute('style');
      const position = el.style.getPropertyValue('position');
      const priority = el.style.getPropertyPriority('position');
      const changed = getComputedStyle(el).position === 'static';
      if (changed) el.style.setProperty('position', 'relative');
      host = { position, priority, hadStyle, changed, layers: new Set() };
      surfaces.set(el, host);
    }
    // Bound layers even during rapid repeated input.
    if (host.layers.size >= 2) return;
    const layer = document.createElement('span');
    layer.className = 'ts-motion-surface';
    layer.setAttribute('aria-hidden', 'true');
    const beam = document.createElement('span');
    beam.className = type === 'ripple' ? 'ts-motion-ripple' : 'ts-motion-beam';
    layer.append(beam);
    el.append(layer);
    host.layers.add(layer);
    const remove = () => {
      layer.remove();
      host.layers.delete(layer);
      if (host.layers.size) return;
      if (host.changed && el.style.getPropertyValue('position') === 'relative') {
        if (host.position) el.style.setProperty('position', host.position, host.priority);
        else el.style.removeProperty('position');
        if (!host.hadStyle && !el.style.length) el.removeAttribute('style');
      }
      surfaces.delete(el);
    };
    let animation;
    if (type === 'ripple') {
      const rect = el.getBoundingClientRect();
      const x = event?.clientX > 0 ? event.clientX - rect.left : rect.width / 2;
      const y = event?.clientY > 0 ? event.clientY - rect.top : rect.height / 2;
      beam.style.left = `${Math.max(0, Math.min(rect.width, x))}px`;
      beam.style.top = `${Math.max(0, Math.min(rect.height, y))}px`;
      animation = play(beam, [
        { transform: 'scale(.2)', opacity: .65 },
        { transform: `scale(${Math.max(rect.width, rect.height) / 14})`, opacity: 0 }
      ], { duration: 460, easing: 'cubic-bezier(.2,.65,.3,1)' });
    } else {
      animation = play(beam, [
        { transform: 'translateX(0) skewX(-18deg)', opacity: 0 },
        { transform: 'translateX(190%) skewX(-18deg)', opacity: subtle ? .14 : .32, offset: .42 },
        { transform: 'translateX(480%) skewX(-18deg)', opacity: 0 }
      ], { duration: subtle ? 640 : 540, easing: 'cubic-bezier(.3,0,.4,1)' });
    }
    if (animation) animation.finished.then(remove, remove);
    else remove();
  }

  function lift(el) {
    if (!usable(el) || hovering.has(el) || !enabled()) return;
    const main = el.matches(primary);
    const end = main ? 'translateY(-2px) scale(1.015)' : 'translateY(-1px)';
    const animation = move(el, ['none', end], { duration: 230, fill: 'forwards' }, true);
    hovering.set(el, { animation, end });
    if (main) {
      light(el, 'shine');
      const shadow = getComputedStyle(el).boxShadow;
      play(el, [{ boxShadow: shadow }, { boxShadow: `${shadow === 'none' ? '' : `${shadow}, `}0 8px 22px #e02d403b` }, { boxShadow: shadow }], { duration: 650 });
    }
  }
  function lower(el) {
    const state = hovering.get(el);
    if (!state) return;
    state.animation?.cancel();
    hovering.delete(el);
    move(el, [state.end, 'none'], { duration: 190 });
  }
  function press(el, event) {
    if (!usable(el)) return;
    move(el, ['none', 'scale(.97)', 'scale(1.008)', 'none'], { duration: 310 });
    if (el.matches(primary)) light(el, 'ripple', event);
  }

  function endTilt() {
    if (!tilt) return;
    const previous = tilt;
    tilt = null;
    cancelAnimationFrame(tiltFrame);
    tiltFrame = 0;
    previous.animation?.cancel();
    move(previous.el, [previous.transform, 'none'], { duration: 240 });
  }
  function tiltTick() {
    tiltFrame = 0;
    if (!tilt || !enabled()) return;
    tilt.x += (tilt.tx - tilt.x) * .22;
    tilt.y += (tilt.ty - tilt.y) * .22;
    tilt.transform = `perspective(1000px) translateY(-2px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`;
    tilt.animation?.effect.setKeyframes([{ transform: tilt.transform }, { transform: tilt.transform }]);
    if (Math.abs(tilt.tx - tilt.x) + Math.abs(tilt.ty - tilt.y) > .025) tiltFrame = requestAnimationFrame(tiltTick);
  }
  function pointCard(el, event) {
    if (!usable(el) || el.matches(':focus-within') || el.querySelector('[dragging]')) return;
    if (!tilt || tilt.el !== el) {
      endTilt();
      const animation = move(el, ['none', 'none'], { duration: 1, fill: 'both' }, true);
      if (!animation) return;
      animation.pause();
      animation.currentTime = 0;
      tilt = { el, animation, rect: el.getBoundingClientRect(), x: 0, y: 0, tx: 0, ty: 0, transform: 'none' };
      light(el.querySelector('.card-gallery') || el, 'shine', null, true);
    }
    const r = tilt.rect;
    tilt.tx = Math.max(-2, Math.min(2, -(event.clientY - r.top - r.height / 2) / Math.max(r.height, 1) * 4));
    tilt.ty = Math.max(-2.5, Math.min(2.5, (event.clientX - r.left - r.width / 2) / Math.max(r.width, 1) * 5));
    if (!tiltFrame) tiltFrame = requestAnimationFrame(tiltTick);
  }

  // Scroll progress, not timed entrance triggers. Additive transforms preserve artwork.
  const scrolling = new Map();
  let scrollFrame = 0, lastScrollTime = 0;
  function finishScroll(el, state) {
    state.motion.cancel();
    scrolling.delete(el);
    el.removeAttribute('data-ts-entering');
  }
  function scrollTick(time) {
    scrollFrame = 0;
    if (!enabled()) return;
    const dt = Math.min(50, lastScrollTime ? time - lastScrollTime : 16);
    lastScrollTime = time;
    const blend = 1 - Math.exp(-dt / 85);
    const height = window.innerHeight;
    let settling = false;
    // Read geometry together before updating effects; no layout properties are written.
    const measurements = [...scrolling].map(([el, state]) => ({ el, state, rect: el.getBoundingClientRect() }));
    measurements.forEach(({ el, state, rect }) => {
      if (!el.isConnected || el.matches(':focus-within')) { finishScroll(el, state); return; }
      const top = rect.top - 16 * (1 - state.current);
      const progress = Math.max(0, Math.min(1, (height * .98 - top) / (height * .3)));
      // Once read, content never disappears again when the user scrolls back.
      state.target = Math.max(state.target, progress);
      state.current += (state.target - state.current) * blend;
      if (Math.abs(state.target - state.current) < .0005) state.current = state.target;
      state.motion.currentTime = state.current * 1000;
      if (state.current >= .9995) finishScroll(el, state);
      else if (state.current !== state.target) settling = true;
    });
    if (settling) scrollFrame = requestAnimationFrame(scrollTick);
    else lastScrollTime = 0;
  }
  function scheduleScroll() {
    if (enabled() && scrolling.size && !scrollFrame) scrollFrame = requestAnimationFrame(scrollTick);
  }
  function reveal(el) {
    if (!enabled()) return true;
    if (seen.has(el)) return true;
    seen.add(el);
    if (el.parentElement?.closest('[data-ts-entering]') || el.matches(':focus-within')) return true;
    // The initial screen and already visible content remain immediately available.
    if (el.getBoundingClientRect().top < window.innerHeight * .7) return true;
    const motion = move(el, ['translateY(16px) rotateX(2deg) scale(.995)', 'none'], {
      duration: 1000, easing: 'linear', fill: 'both'
    }, true);
    if (!motion) return true;
    motion.pause();
    motion.currentTime = 0;
    el.setAttribute('data-ts-entering', '');
    scrolling.set(el, { motion, current: 0, target: 0 });
    scheduleScroll();
    return true;
  }
  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      observer.unobserve(entry.target);
      observed.delete(entry.target);
      reveal(entry.target, Number(entry.target.dataset.tsSequence || 0));
    });
  }, { threshold: 0, rootMargin: '0px 0px 300px 0px' }) : null;
  function scan(root = document) {
    if (!enabled() || !root.querySelectorAll) return;
    const list = [...root.querySelectorAll(entrances)];
    if (root instanceof Element && root.matches(entrances)) list.unshift(root);
    list.forEach((el, index) => {
      if (seen.has(el) || observed.has(el) || el.closest('[data-tc-motion="false"]')) return;
      el.dataset.tsSequence = String(index % 4);
      if (observer) { observed.add(el); observer.observe(el); }
    });
  }
  function reset() {
    cancelAnimationFrame(scrollFrame); scrollFrame = 0; lastScrollTime = 0;
    scrolling.forEach((state, el) => finishScroll(el, state));
    endTilt();
    hovering.forEach(state => state.animation?.cancel());
    hovering.clear();
    running.forEach(animation => animation.cancel());
    running.clear();
  }
  function refresh() {
    document.body.classList.toggle('ts-motion-enabled', !disabled && !reduced.matches);
    if (!enabled()) reset(); else scan();
  }
  window.toscaniMotion = {
    reveal,
    setEnabled(value) { disabled = !value; refresh(); },
    get enabled() { return enabled(); }
  };
  document.addEventListener('pointerover', event => {
    if (!enabled() || !fine.matches || event.pointerType !== 'mouse') return;
    const el = closest(event, controls);
    if (el && !(event.relatedTarget instanceof Node && el.contains(event.relatedTarget))) lift(el);
  }, { passive: true });
  document.addEventListener('pointerout', event => {
    const el = closest(event, controls);
    if (el && !(event.relatedTarget instanceof Node && el.contains(event.relatedTarget))) lower(el);
    if (tilt && !(event.relatedTarget instanceof Node && tilt.el.contains(event.relatedTarget))) endTilt();
  }, { passive: true });
  document.addEventListener('pointermove', event => {
    if (!enabled() || !fine.matches || event.pointerType !== 'mouse') return;
    const el = closest(event, cards);
    if (el && !closest(event, 'button,input,select,summary')) pointCard(el, event);
    else if (tilt) endTilt();
  }, { passive: true });
  document.addEventListener('pointerdown', event => {
    if (!enabled() || event.button !== 0) return;
    endTilt();
    const el = closest(event, controls);
    if (el) press(el, event);
    else if (event.pointerType === 'touch') {
      const card = closest(event, cards);
      if (card && !closest(event, 'input,select,summary,button')) {
        move(card, ['none', 'scale(.994)', 'none'], { duration: 220 });
        light(card.querySelector('.card-gallery') || card, 'shine', event, true);
      }
    }
  }, { passive: true });
  document.addEventListener('click', event => {
    if (event.detail !== 0 || !enabled()) return;
    const el = closest(event, controls);
    if (el) press(el, event);
  }, { passive: true });
  document.addEventListener('focusin', event => {
    endTilt();
    const el = closest(event, primary);
    if (el?.matches(':focus-visible')) light(el, 'shine');
  });
  document.addEventListener('toggle', event => {
    if (!enabled() || !event.target.open) return;
    const content = event.target.querySelector?.(':scope > .details-content');
    if (content) move(content, ['translateY(-5px)', 'none'], { duration: 250 });
  }, true);
  document.addEventListener('pointercancel', reset, { passive: true });
  window.addEventListener('scroll', () => { endTilt(); scheduleScroll(); }, { passive: true });
  window.addEventListener('resize', () => { endTilt(); scheduleScroll(); }, { passive: true });
  window.addEventListener('blur', reset);
  window.addEventListener('pagehide', reset);
  window.addEventListener('pageshow', refresh);
  document.addEventListener('visibilitychange', refresh);
  reduced.addEventListener('change', refresh);
  fine.addEventListener('change', reset);
  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopify:section:unload', reset);
  document.addEventListener('shopify:section:select', reset);
  // Native product filtering can replace cards without reloading the page.
  const main = document.getElementById('MainContent');
  if (main && 'MutationObserver' in window) new MutationObserver(records => {
    records.forEach(record => {
      record.addedNodes.forEach(node => {
        if (node instanceof Element && !node.matches('.ts-motion-surface,.ts-motion-beam,.ts-motion-ripple')) scan(node);
      });
      record.removedNodes.forEach(node => {
        if (!(node instanceof Element)) return;
        observed.forEach(el => { if (node === el || node.contains(el)) { observer?.unobserve(el); observed.delete(el); } });
      });
    });
  }).observe(main, { childList: true, subtree: true });
  refresh();
})();
